import { useEffect, useMemo, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { emptyPoItemRow, poItemColumnList, syncPoItemDraft } from "../data";
import PoColumnField from "../PoColumnField";
import { poColors, poFieldControlSx, poFieldLabelSx, poPrimaryBtnSx, poSecondaryBtnSx } from "../poUi";

/** Modal layout order: barcode+qty row, then item description, then the rest. */
const ADD_MODAL_FIELD_ORDER = ["barcode", "qty", "item_description", "item_code", "rate", "due_date", "remarks"];

const fieldGridSx = (key) => {
	if (key === "barcode") return { gridColumn: { xs: "1 / -1", sm: "1 / 4" } };
	if (key === "qty") return { gridColumn: { xs: "1 / 2", sm: "4 / 5" } };
	if (key === "item_description") return { gridColumn: "1 / -1" };
	if (key === "item_code") return { gridColumn: { xs: "1 / -1", sm: "1 / 3" } };
	if (key === "rate") return { gridColumn: { xs: "1 / 2", sm: "3 / 4" } };
	if (key === "due_date") return { gridColumn: { xs: "1 / 2", sm: "4 / 5" } };
	if (key === "remarks") return { gridColumn: "1 / -1" };
	return { gridColumn: { xs: "1 / -1", sm: "span 1" } };
};

const PoLineItemsAddModal = ({ open, onClose, onAdd }) => {
	const [draftItem, setDraftItem] = useState(emptyPoItemRow());
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (open) {
			setDraftItem(emptyPoItemRow());
			setSaving(false);
		}
	}, [open]);

	const setDraftItemSynced = (updater) => {
		setDraftItem((prev) => {
			const next = typeof updater === "function" ? updater(prev) : updater;
			return syncPoItemDraft(next);
		});
	};

	const handleClose = () => {
		if (saving) return;
		onClose?.();
	};

	const handleAdd = async () => {
		if (saving) return;
		setSaving(true);
		try {
			const ok = await onAdd?.(syncPoItemDraft(draftItem));
			if (ok !== false) {
				setDraftItem(emptyPoItemRow());
				onClose?.();
			}
		} finally {
			setSaving(false);
		}
	};

	const modalColumns = useMemo(() => {
		const byKey = Object.fromEntries(poItemColumnList.map((col) => [col.key, col]));
		return ADD_MODAL_FIELD_ORDER.map((key) => byKey[key]).filter(Boolean);
	}, []);

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle sx={{ fontSize: 17, fontWeight: 700 }}>Add item</DialogTitle>
			<DialogContent sx={{ pt: 1.5 }}>
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr 1fr" },
						columnGap: 1.5,
						rowGap: 1.5,
						pt: 0.5,
					}}>
					{modalColumns.map((column) => {
						const required = !!column?.constraints?.NOTNULL;
						return (
							<Box key={column.key} sx={{ ...fieldGridSx(column.key), minWidth: 0 }}>
								<Typography component="label" sx={poFieldLabelSx}>
									{column.name}
									{required ? (
										<Box component="span" sx={{ color: poColors.danger, ml: 0.25 }}>
											*
										</Box>
									) : null}
								</Typography>
								<Box sx={poFieldControlSx}>
									<PoColumnField
										column={column}
										data={draftItem}
										setData={setDraftItemSynced}
										readOnly={!!column.disabled}
										dense
										hideLabel
										width="100%"
									/>
								</Box>
							</Box>
						);
					})}
				</Box>
			</DialogContent>
			<DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
				<Button variant="outlined" onClick={handleClose} disabled={saving} sx={poSecondaryBtnSx}>
					Cancel
				</Button>
				<Button variant="contained" onClick={handleAdd} disabled={saving} sx={poPrimaryBtnSx} autoFocus>
					Add
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default PoLineItemsAddModal;
